import { takeUntil } from 'rxjs';

import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { DocumentReference } from '@angular/fire/firestore';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@auth/services/auth/auth.service';
import { IInventoryLocationItemWithData } from '@inventory/models/inventory';
import { IInventoryAdjustStockForm } from '@inventory/models/inventory-forms';
import { InventoryStockAdjustmentService } from '@inventory/services/inventory-stock-adjustment/inventory-stock-adjustment.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { DropdownControlComponent, IDropdownOption } from '@shared/components/forms/dropdown-control/dropdown-control.component';
import { StandardControlComponent } from '@shared/components/forms/standard-control/standard-control.component';
import { TextareaControlComponent } from '@shared/components/forms/textarea-control/textarea-control.component';
import { ToastTypes } from '@shared/models/toast';
import { ToastService } from '@shared/services/toast/toast.service';

@Component({
	selector: 'app-adjust-stock-modal',
	imports: [CommonModule, DropdownControlComponent, StandardControlComponent, TextareaControlComponent],
	templateUrl: './adjust-stock-modal.component.html',
	styleUrl: './adjust-stock-modal.component.scss',
})
export class AdjustStockModalComponent implements OnInit, OnDestroy {
	@Input() organisationRef: DocumentReference;
	@Input() inventoryItemLocation: IInventoryLocationItemWithData;

	private authService = inject(AuthService);
	private activeModal = inject(NgbActiveModal);
	private toastService = inject(ToastService);
	private inventoryStockAdjustmentService = inject(InventoryStockAdjustmentService);

	destroyed$: EventEmitter<void> = new EventEmitter<void>();

	form: FormGroup<IInventoryAdjustStockForm>;
	changeTypeOptions: Array<IDropdownOption> = [
		{
			id: 'increase',
			label: 'Increase',
			value: 'increase',
			icon: 'bi-plus-circle',
		},
		{
			id: 'decrease',
			label: 'Decrease',
			value: 'decrease',
			icon: 'bi-dash-circle',
		},
	];

	isAddingStock = false;
	newQuantity: number;

	ngOnInit(): void {
		this.form = new FormGroup<IInventoryAdjustStockForm>({
			changeType: new FormControl<'increase' | 'decrease' | null>('increase', [Validators.required]),
			quantity: new FormControl<number | null>(null, [Validators.required, Validators.min(1)]),
			reason: new FormControl<string | null>(null),
		});

		this.newQuantity = this.inventoryItemLocation.quantity;

		this.form.valueChanges.pipe(takeUntil(this.destroyed$)).subscribe((values) => {
			let adjustment: number = Number(values.quantity ?? 0);
			if (values.changeType === 'decrease') {
				adjustment = -1 * adjustment;
			}
			this.newQuantity = this.inventoryItemLocation.quantity + adjustment;
		});
	}

	closeModal(): void {
		this.activeModal.close();
	}

	ngOnDestroy(): void {
		this.destroyed$.emit();
	}

	get quantity(): FormControl<number | null> {
		return this.form.controls.quantity;
	}

	get changeType(): FormControl<'increase' | 'decrease' | null> {
		return this.form.controls.changeType;
	}

	get reason(): FormControl<string | null> {
		return this.form.controls.reason;
	}

	submit(): void {
		if (this.form.invalid || this.newQuantity < 0) {
			this.form.markAllAsTouched();
			return;
		}

		this.isAddingStock = true;

		this.inventoryStockAdjustmentService
			.createInventoryStockAdjustment(
				this.organisationRef,
				this.authService.currentUser?.uid,
				this.inventoryItemLocation.inventoryItem,
				this.inventoryItemLocation.location,
				this.quantity.value as number,
				this.changeType.value as 'increase' | 'decrease',
				this.reason.value,
			)
			.then(() => {
				this.closeModal();
				this.toastService.showToast({
					title: 'Stock Adjusted',
					message: 'The stock adjustment has been recorded successfully.',
					type: ToastTypes.SUCCESS,
				});
			})
			.catch((error) => {
				console.error(error);
				this.toastService.showToast({
					title: 'Error',
					message: 'An error occurred while recording the stock adjustment. Please try again.',
					type: ToastTypes.ERROR,
				});
			})
			.finally(() => {
				this.isAddingStock = false;
			});
	}
}
